/**
 * Semantic operational modes for VoiceBot (React-facing).
 * React must not reference vbBench, vbRecord, or other VoiceBot-internal flags.
 */

export const VOICEBOT_MODES = Object.freeze({
  PRODUCTION: 'production',
  BENCH: 'bench',
  RECORD: 'record',
  REPLAY: 'replay',
  BASELINE: 'baseline',
  OVERHEAD: 'overhead',
});

const ALLOWED_MODES = new Set(Object.values(VOICEBOT_MODES));

const ENGINEERING_RIG_MODES = new Set([
  VOICEBOT_MODES.BENCH,
  VOICEBOT_MODES.RECORD,
  VOICEBOT_MODES.REPLAY,
  VOICEBOT_MODES.BASELINE,
  VOICEBOT_MODES.OVERHEAD,
]);

/**
 * True when the avatar host is in a Bench / Record / Replay / Baseline / Overhead
 * operational mode whose engineering panel must stay visually unobstructed.
 * Production (including envelope-only) is not an engineering rig.
 */
export function isVoiceBotEngineeringRigActive(mode) {
  return ENGINEERING_RIG_MODES.has(mode);
}

function isDevRuntime() {
  if (process.env.NODE_ENV === 'development') return true;
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  return host === 'localhost' || host === '127.0.0.1';
}

/**
 * Resolve operational mode from runtime configuration.
 * Production deployments default to production unless dev runtime + explicit parent flag.
 */
export function resolveOperationalMode() {
  if (!isDevRuntime()) {
    return VOICEBOT_MODES.PRODUCTION;
  }

  if (typeof window === 'undefined') {
    return VOICEBOT_MODES.PRODUCTION;
  }

  const params = new URLSearchParams(window.location.search);
  const requested = (params.get('voicebotMode') || '').trim().toLowerCase();

  if (requested && ALLOWED_MODES.has(requested) && requested !== VOICEBOT_MODES.PRODUCTION) {
    return requested;
  }

  return VOICEBOT_MODES.PRODUCTION;
}
